import { Trans } from '@lingui/macro';
import {
  ActionIcon,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'tabler-icons-react';

import { TimeBlock } from '../../components/train-route-info/train-route-info';
import { UiCard } from '../../components/ui/ui-card/ui-card';
import { useOrders } from '../../domains/cabinet/features/orders/orders.provider';
import { EOrderStatus } from '../../domains/orders/core/order';
import { UserTimeZoneNotify } from '../../domains/tz-notify/tz-notify';
import { MainSideMenu } from '../../features/main-side-menu/main-side-menu';
import { getNameLocal } from '../../lib/local/get-title';
import { useLocal } from '../../lib/local/use-local';
import { routeCabinet } from '../../router/routes';

import styles from './my-orders.page.module.css';

const orderStatus = (type: number) => {
  switch (type) {
    case EOrderStatus.new:
      return (
        <Text
          sx={() => ({
            fontSize: 13,
            color: 'white',
            background: '#FFCC41',
            padding: '2px 6px ',
            borderRadius: 4,
          })}
        >
          <Trans>Ожидает оплаты</Trans>
        </Text>
      );

    case EOrderStatus.success:
      return (
        <Text
          sx={() => ({
            fontSize: 13,
            color: 'white',
            background: '#37b237',
            padding: '2px 6px ',
            borderRadius: 4,
          })}
        >
          <Trans>Оплачен</Trans>
        </Text>
      );

    case EOrderStatus.error:
      return (
        <Text
          sx={() => ({
            fontSize: 13,
            color: 'white',
            background: 'red',
            padding: '2px 6px ',
            borderRadius: 4,
          })}
        >
          <Trans>Ошибка</Trans>
        </Text>
      );
    case EOrderStatus.booked:
      return (
        <Text
          sx={() => ({
            fontSize: 13,
            color: 'white',
            background: '#FFCC41',
            padding: '2px 6px ',
            borderRadius: 4,
          })}
        >
          <Trans>Забронирован</Trans>
        </Text>
      );
    case EOrderStatus.canceled:
      return (
        <Text
          sx={() => ({
            fontSize: 13,
            color: 'white',
            background: '#FFCC41',
            padding: '2px 6px ',
            borderRadius: 4,
          })}
        >
          <Trans>Отменен</Trans>
        </Text>
      );
  }
};

export const MyOrdersPage = observer(() => {
  const navigate = useNavigate();
  const [lang] = useLocal();
  const { getOrdersRequest, ordersStore } = useOrders();
  const isLoading = getOrdersRequest.isWaitingForLoad;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.backButton}>
          <ActionIcon
            variant="transparent"
            radius="50%"
            size="40px"
            bg="transparent"
            onClick={() => navigate(routeCabinet.path)}
          >
            <ArrowLeft size={24} strokeWidth={2} color="white" />
          </ActionIcon>
        </div>
        <div className={styles.routeTitle}>
          <h1 className={styles.headerTitle}>
            <Trans>Мои заказы</Trans>
          </h1>
        </div>
        <div className={styles.mainSideMenu}>
          <MainSideMenu />
        </div>
      </header>
      <main className={styles.main}>
        <Box pos="relative" mih={200}>
          <LoadingOverlay visible={isLoading} />

          {ordersStore.orders.length === 0 && !isLoading && (
            <UiCard>
              <Trans>Заказы пока что отсутствуют</Trans>
            </UiCard>
          )}

          {ordersStore.orders.length > 0 && (
            <Stack spacing="sm">
              <UserTimeZoneNotify />

              {ordersStore.orders.map((order) => (
                <UiCard key={order.id}>
                  <Stack spacing="sm">
                    <Flex align="center" justify="space-between">
                      <Text size={13} fw={700}>
                        {dayjs(order.date).format('DD.MM.YYYY HH:mm')}
                      </Text>
                      {orderStatus(order.status)}
                    </Flex>

                    {order.tickets.map((ticket) =>
                      ticket.mainTicketId == undefined ? (
                        <Grid
                          key={ticket.id}
                          align="center"
                          justify="center"
                          gutter={10}
                          mt={8}
                        >
                          <Grid.Col span="content">
                            <TimeBlock
                              target={getNameLocal(lang, ticket.stationFrom)}
                              date={new Date(ticket.departureTime)}
                            />
                          </Grid.Col>
                          <Grid.Col span="auto">
                            <Divider my={4} />
                          </Grid.Col>
                          <Grid.Col span="content">
                            <TimeBlock
                              target={getNameLocal(lang, ticket.stationTo)}
                              date={new Date(ticket.arrivalTime)}
                              direction="end"
                            />
                          </Grid.Col>
                        </Grid>
                      ) : null
                    )}

                    <Flex justify="space-between" align="center">
                      <Text span size="xl" fw={700}>
                        <Trans>Сумма:</Trans>
                      </Text>
                      <Text span size="xl" fw={700}>
                        {order.paySumm} ֏
                      </Text>
                    </Flex>
                    <Button
                      size="lg"
                      onClick={() =>
                        navigate(routeCabinet.ORDER.buildPath({ id: order.id }))
                      }
                    >
                      <Trans>Показать заказ</Trans>
                    </Button>
                  </Stack>
                </UiCard>
              ))}
            </Stack>
          )}
        </Box>
      </main>
    </div>
  );
});
